import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { Button, Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import parsingPageStore from "./ParsingPageStore";
import { getAnalyzeActAPIUrl, getAPIUrl } from "../api/apiUtils";
import "./ParsingPage.scss";
import { useRegionsData } from "../hooks/useRegions";
import GroupSelector from "../components/GroupSelector";
import PaginationControls from "../components/PaginationControls";
import usePagination from "../hooks/usePagination";
import { apiFetch } from "../api/apiClient";

const API_BASE_URL = getAPIUrl();
const ANALYZE_ACT_API_BASE_URL = getAnalyzeActAPIUrl();
const YOUTUBE_API_BASE_URL = `${API_BASE_URL}/youtube`;
const NEWS_SOURCE_OPTIONS = [
  { id: "rbc", label: "РБК" },
  { id: "ria", label: "РИА Новости" },
  { id: "mail", label: "Новости Mail.Ru" },
  { id: "lenta", label: "Лента.ру" },
  { id: "rambler", label: "Rambler" },
];
const NEWS_SOURCE_LABELS = NEWS_SOURCE_OPTIONS.reduce((acc, source) => {
  acc[source.id] = source.label;
  return acc;
}, {});
const NEWS_SOURCE_IDS_BY_LABEL = NEWS_SOURCE_OPTIONS.reduce((acc, source) => {
  acc[source.label] = source.id;
  return acc;
}, {});
const getNewsSourceLabel = (source) => {
  const sourceId = String(source || "").trim();
  return NEWS_SOURCE_LABELS[sourceId] || sourceId || "-";
};
const castNewsSourceForKafka = (source) => {
  const sourceName = String(source || "").trim();
  return NEWS_SOURCE_IDS_BY_LABEL[sourceName] || sourceName;
};
const getYoutubeSourceLabel = (source) => {
  const sourceValue = String(source || "").trim();
  if (!sourceValue) return "-";

  const handleMatch = sourceValue.match(/(?:^|[/?&])@([A-Za-z0-9._-]+)/);
  if (handleMatch) {
    return `@${handleMatch[1]}`;
  }

  try {
    const url = new URL(sourceValue);
    const pathParts = url.pathname.split("/").filter(Boolean);
    const handlePart = pathParts.find((part) => part.startsWith("@"));
    if (handlePart) return handlePart;
    if (/(^|\.)youtu(be\.com|\.be)$/i.test(url.hostname) && pathParts.length > 0) {
      return pathParts[pathParts.length - 1];
    }
  } catch {
    // Keep non-URL values as entered.
  }

  return sourceValue;
};
const MIN_TG_DATE = "1970-01-01";
const MAX_TG_DATE = "9999-12-31";
const MOSCOW_TIME_ZONE = "Europe/Moscow";
const YOUTUBE_INITIAL_FORM = {
  source: "",
  api_key: "",
  start_date: "",
  end_date: "",
  include_comments: true,
  force_refresh: false,
  max_videos: "",
  max_comment_threads_per_video: "",
  is_periodic: false,
  interval_value: "",
  interval_unit: "hours",
  repeat_forever: false,
  repeat_count: "",
  overlap_value: "0",
  overlap_unit: "minutes",
};

const TASK_TYPE_LABELS = {
  parser: "ВК",
  news: "Новости",
  tg: "Telegram",
  youtube: "YouTube",
};

const TASK_STATUS_LABELS = {
  scheduled: "Запланирована",
  running: "Выполняется",
  completed: "Завершена",
  failed: "Ошибка",
  failed_enqueue: "Ошибка очереди",
  cancelled: "Отменена",
  queued: "В очереди",
  unknown: "Неизвестно",
};

const TASK_STATUS_VARIANTS = {
  scheduled: "primary",
  running: "info",
  completed: "success",
  failed: "danger",
  failed_enqueue: "danger",
  cancelled: "secondary",
  queued: "secondary",
  unknown: "dark",
};

const formatRepeatCount = (task) => {
  if (task?.repeat_forever || Number(task?.max_repeats) < 0) {
    return "\u221E";
  }
  return task?.max_repeats ?? "не задано";
};

const pluralizeRu = (value, forms) => {
  const absValue = Math.abs(Number(value));
  const mod10 = absValue % 10;
  const mod100 = absValue % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms[1];
  return forms[2];
};

const intervalUnitForms = {
  minutes: ["минуту", "минуты", "минут"],
  hours: ["час", "часа", "часов"],
  days: ["день", "дня", "дней"],
  weeks: ["неделю", "недели", "недель"],
};

const formatEveryInterval = (value, unit) => {
  return `раз в ${value} ${pluralizeRu(value, intervalUnitForms[unit])}`;
};

const formatTaskInterval = (task) => {
  if (!isPeriodicTask(task)) return "однократно";

  const intervalValue = Number(task?.interval_value);
  const intervalUnit = task?.interval_unit;
  if (Number.isFinite(intervalValue) && intervalValue > 0 && intervalUnitForms[intervalUnit]) {
    return formatEveryInterval(intervalValue, intervalUnit);
  }

  const minutes = Number(task?.interval_minutes ?? task?.interval ?? 0);
  if (!Number.isFinite(minutes) || minutes <= 0) return "однократно";
  if (minutes % 10080 === 0) {
    const value = minutes / 10080;
    return formatEveryInterval(value, "weeks");
  }
  if (minutes % 1440 === 0) {
    const value = minutes / 1440;
    return formatEveryInterval(value, "days");
  }
  if (minutes % 60 === 0) {
    const value = minutes / 60;
    return formatEveryInterval(value, "hours");
  }
  return formatEveryInterval(minutes, "minutes");
};

const formatOverlap = (task) => {
  const value = Number(task?.overlap_value ?? 0);
  const unit = task?.overlap_unit ?? "minutes";
  if (!Number.isFinite(value) || value <= 0) return "нет";
  return `${value} ${pluralizeRu(value, intervalUnitForms[unit] || intervalUnitForms.minutes)}`;
};

const isPeriodicTask = (task) => {
  const interval = Number(task?.interval_minutes ?? task?.interval ?? 0);
  return ["parser", "news", "tg", "youtube"].includes(task?.type) && (task?.repeat_forever || interval > 0);
};

const sanitizeDateValue = (value) => {
  if (!value) return "";
  const [year = "", month = "", day = ""] = String(value).split("-");
  return [year.slice(0, 4), month.slice(0, 2), day.slice(0, 2)]
    .filter(Boolean)
    .join("-");
};

const isValidDateInput = (value) => /^\d{4}-\d{2}-\d{2}$/.test(value);

const parseUtcDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === "number") {
    return new Date(value > 1000000000000 ? value : value * 1000);
  }

  const rawValue = String(value).trim();
  if (!rawValue) return null;
  if (/^\d+$/.test(rawValue)) {
    const numericValue = Number(rawValue);
    return new Date(numericValue > 1000000000000 ? numericValue : numericValue * 1000);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(rawValue)) {
    return new Date(`${rawValue}T00:00:00Z`);
  }

  const hasExplicitTimeZone = /(?:GMT|UTC|Z|[+-]\d{2}:?\d{2})$/i.test(rawValue);
  if (hasExplicitTimeZone) {
    const parsedValue = Date.parse(rawValue);
    if (!Number.isNaN(parsedValue)) {
      return new Date(parsedValue);
    }
  }

  const normalizedValue = rawValue.replace(" ", "T");
  return new Date(`${normalizedValue}Z`);
};

const formatMoscowDateTime = (value, emptyValue = "-") => {
  if (!value) return emptyValue;
  const date = parseUtcDateTime(value);
  if (!date || Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: MOSCOW_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
};

const toTimestamp = (value) => {
  const date = parseUtcDateTime(value);
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const normalizeTaskStatus = (task) => String(task?.status || "unknown").toLowerCase();

const getTaskType = (task) => task?.type || task?.data?.type || "parser";

const getTaskErrorText = (task) => {
  const error =
    task?.last_error ||
    task?.error ||
    task?.data?.error ||
    task?.result?.error?.user_action ||
    task?.result?.error?.message;
  if (!error) return "";
  return typeof error === "string" ? error : JSON.stringify(error);
};

const getTaskLastRunValue = (task) => {
  if (task?.last_run_at || task?.last_run) {
    return task.last_run_at || task.last_run;
  }

  const status = normalizeTaskStatus(task);
  if (["running", "completed", "failed"].includes(status)) {
    return task?.last_success_at || task?.last_status_at || task?.updated_at;
  }

  return null;
};

const getNextRunState = (task) => {
  const nextRunTs = toTimestamp(task?.next_run);
  if (!nextRunTs) return "empty";
  if (normalizeTaskStatus(task) === "running") return "running";
  const diffMs = nextRunTs - Date.now();
  if (diffMs < 0) return "overdue";
  if (diffMs <= 15 * 60 * 1000) return "soon";
  return "scheduled";
};

const compareCollectionTasks = (a, b) => {
  const aPeriodic = isPeriodicTask(a);
  const bPeriodic = isPeriodicTask(b);
  if (aPeriodic !== bPeriodic) {
    return aPeriodic ? -1 : 1;
  }

  if (aPeriodic && bPeriodic) {
    const nextRunDiff = toTimestamp(a.next_run) - toTimestamp(b.next_run);
    if (nextRunDiff !== 0) return nextRunDiff;
  }

  return (
    toTimestamp(b.last_run_at || b.last_run || b.timestamp) -
    toTimestamp(a.last_run_at || a.last_run || a.timestamp)
  );
};

const readErrorMessage = async (response) => {
  const responseText = await response.text();
  if (!responseText) {
    return `${response.status} ${response.statusText}`.trim();
  }

  try {
    const parsed = JSON.parse(responseText);
    return parsed.error || parsed.message || responseText;
  } catch {
    return responseText;
  }
};

const normalizePositiveInteger = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? Math.floor(number) : null;
};

const ParsingPage = observer(() => {
  const { regions: groups, isLoading: isGroupsLoading } = useRegionsData();
  const [tasks, setTasks] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSection, setActiveSection] = useState("collection");
  const [newsTaskForm, setNewsTaskForm] = useState({
    source: NEWS_SOURCE_OPTIONS[0].label,
    start_date: "",
    end_date: "",
    is_periodic: false,
    interval_value: "",
    interval_unit: "hours",
    repeat_forever: false,
    repeat_count: "",
    overlap_value: "0",
    overlap_unit: "minutes",
  });
  const [isNewsTaskSubmitting, setIsNewsTaskSubmitting] = useState(false);
  const [tgTaskForm, setTgTaskForm] = useState({
    channel: "",
    start_date: "",
    end_date: "",
    is_periodic: false,
    interval_value: "",
    interval_unit: "hours",
    repeat_forever: false,
    repeat_count: "",
    overlap_value: "0",
    overlap_unit: "minutes",
    use_coverage: true,
    include_discussion: true,
    discussion_limit_per_post: "",
    max_posts_for_discussion: "",
  });
  const [isTgTaskSubmitting, setIsTgTaskSubmitting] = useState(false);
  const [tgTaskResult, setTgTaskResult] = useState(null);
  const [tgAuthStatus, setTgAuthStatus] = useState(null);
  const [isTgAuthLoading, setIsTgAuthLoading] = useState(false);
  const [tgAuthForm, setTgAuthForm] = useState({
    phone: "",
    auth_id: "",
    code: "",
    password: "",
    ttl_minutes: 10,
  });
  const [tgAuthNeedsPassword, setTgAuthNeedsPassword] = useState(false);
  const [youtubeTaskForm, setYoutubeTaskForm] = useState(YOUTUBE_INITIAL_FORM);
  const [isYoutubeTaskSubmitting, setIsYoutubeTaskSubmitting] = useState(false);
  const [deletingTaskId, setDeletingTaskId] = useState(null);
  const [taskSearch, setTaskSearch] = useState("");
  const [taskStatusFilter, setTaskStatusFilter] = useState("");
  const [taskTypeFilter, setTaskTypeFilter] = useState("");
  const [taskPeriodicFilter, setTaskPeriodicFilter] = useState("all");

  const fetchTasks = async () => {
    try {
      const tasksRes = await apiFetch(`${API_BASE_URL}/common/tasks`);

      if (!tasksRes.ok) {
        throw new Error("Не удалось загрузить данные по задачам.");
      }

      const tasksData = await tasksRes.json();
      setTasks(tasksData);
    } catch (error) {
      console.error("Ошибка загрузки задач:", error);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const getGroupTitle = (groupId) => {
    const normalizedGroupId = String(groupId);
    for (const region of groups) {
      const group = region.groups.find(
        (g) => String(g.group_id) === normalizedGroupId || String(g.id) === normalizedGroupId
      );
      if (group) return group.title;
    }
    return "";
  };

  const getTaskGroupId = (task) => {
    if (task?.type === "news") {
      return task?.data?.source || task?.source || "news";
    }
    if (task?.type === "tg") {
      return task?.data?.channel || task?.channel || "tg";
    }
    if (task?.type === "youtube") {
      return getYoutubeSourceLabel(task?.data?.source || task?.source || "youtube");
    }

    const explicitGroupId =
      task?.data?.community_id ||
      task?.data?.group_id ||
      task?.community_id ||
      task?.group_id;
    if (explicitGroupId) return String(explicitGroupId);

    const taskId = String(task?.task_id || "");
    const match = taskId.match(/^-?\d+/);
    return match ? match[0] : "-";
  };

  const getTaskGroupTitle = (task) => {
    if (task?.type === "news") {
      return getNewsSourceLabel(task?.data?.source || task?.source);
    }

    if (task?.type === "tg") {
      return task?.data?.channel || task?.channel || "-";
    }
    if (task?.type === "youtube") {
      return getYoutubeSourceLabel(task?.data?.source || task?.source);
    }

    const groupId = getTaskGroupId(task);
    return task?.data?.title || getGroupTitle(groupId) || "-";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (selectedGroups.length === 0) {
      alert("Выберите хотя бы одну группу перед запуском задачи.");
      return;
    }

    if (
      parsingPageStore.isPeriodic &&
      (!parsingPageStore.intervalValue || !parsingPageStore.intervalUnit)
    ) {
      alert("Укажите интервал и единицы измерения для периодической задачи.");
      return;
    }

    if (
      parsingPageStore.isPeriodic &&
      !parsingPageStore.repeatForever &&
      (!parsingPageStore.reps || Number(parsingPageStore.reps) < 1)
    ) {
      alert("Укажите количество повторов или включите бесконечный режим.");
      return;
    }

    try {
      const formDataBase = new FormData(event.target);
      formDataBase.set("start_type", "current");
      if (parsingPageStore.isPeriodic) {
        formDataBase.set("depth", "1");
        formDataBase.set("repeat_forever", parsingPageStore.repeatForever ? "true" : "false");
        if (parsingPageStore.repeatForever) {
          formDataBase.set("repeat_count", "-1");
        }
      }
      
      let userId = null;
      try {
        const userStr =
          typeof window !== "undefined" && window.localStorage
            ? window.localStorage.getItem("user")
            : null;
        if (userStr) {
          const userObj = JSON.parse(userStr);
          if (userObj && userObj.id) {
            userId = Number(userObj.id);
          }
        }
      } catch (e) {
        console.error("Ошибка парсинга user из localStorage:", e);
      }
      
      const userLogin =
        (typeof window !== "undefined" && window.localStorage
          ? window.localStorage.getItem("user_login") ||
            window.localStorage.getItem("login") ||
            window.localStorage.getItem("username")
          : null) || "";
      const groupsToRun = [...selectedGroups];

      for (const groupId of groupsToRun) {
        const groupTitle = getGroupTitle(groupId);
        if (!groupTitle) {
          console.warn(`Не удалось определить название группы ${groupId}, задача пропущена.`);
          continue;
        }

        const formData = new FormData();
        for (const [key, value] of formDataBase.entries()) {
          formData.append(key, value);
        }

        formData.set("community_id", groupId);
        formData.set("title", groupTitle);
        if (userId) {
          formData.set("user_id", userId);
        }
        if (userLogin) {
          formData.set("user_login", userLogin);
        }

        const response = await apiFetch(`${ANALYZE_ACT_API_BASE_URL}/tasks/submit_task`, {
          method: "POST",
          body: formData,
        });

        const responseText = await response.text();
        if (!response.ok) {
          let errorMessage = responseText;
          try {
            const parsedError = JSON.parse(responseText);
            errorMessage = parsedError.error || parsedError.message || responseText;
          } catch {
            // Keep raw response text when backend does not return JSON.
          }
          throw new Error(
            `Ошибка отправки для группы ${groupId}: ${response.status} - ${errorMessage}`
          );
        }
      }

      alert("Задачи успешно созданы для всех выбранных групп.");
      await fetchTasks();
      setSelectedGroups([]);
    } catch (error) {
      console.error("Ошибка создания задач:", error);
      alert(error.message || "Не удалось создать одну или несколько задач. Подробности в консоли.");
    }
  };

  const handleDeletePeriodicTask = async (task) => {
    if (!task?.task_id) return;

    const confirmed = window.confirm(
      `Удалить периодическую задачу ${task.task_id}? Она будет убрана из планировщика и списка задач.`
    );
    if (!confirmed) return;

    setDeletingTaskId(task.task_id);
    try {
      const response = await apiFetch(
        `${ANALYZE_ACT_API_BASE_URL}/tasks/task/${encodeURIComponent(task.task_id)}`,
        { method: "DELETE" }
      );

      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      await fetchTasks();
    } catch (error) {
      console.error("Ошибка удаления периодической задачи:", error);
      alert(`Не удалось удалить задачу: ${error.message}`);
    } finally {
      setDeletingTaskId(null);
    }
  };

  const handleNewsTaskInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setNewsTaskForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNewsTaskSubmit = async (event) => {
    event.preventDefault();
    const {
      source,
      start_date,
      end_date,
      is_periodic,
      interval_value,
      interval_unit,
      repeat_forever,
      repeat_count,
      overlap_value,
      overlap_unit,
    } = newsTaskForm;

    if (!source) {
      alert("Выберите группу источника.");
      return;
    }

    if (!is_periodic && (!start_date || !end_date)) {
      alert("Укажите начальную и конечную даты.");
      return;
    }

    if (!is_periodic && new Date(start_date) > new Date(end_date)) {
      alert("Начальная дата не может быть позже конечной.");
      return;
    }

    if (is_periodic && (!interval_value || Number(interval_value) < 1 || !interval_unit)) {
      alert("Укажите интервал для периодической задачи.");
      return;
    }

    if (is_periodic && !repeat_forever && (!repeat_count || Number(repeat_count) < 1)) {
      alert("Укажите количество повторов или включите бесконечный режим.");
      return;
    }

    setIsNewsTaskSubmitting(true);
    try {
      const kafkaSource = castNewsSourceForKafka(source);
      const response = await apiFetch(`${API_BASE_URL}/news_parser/news_tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          source: kafkaSource,
          start_date: is_periodic ? null : start_date,
          end_date: is_periodic ? null : end_date,
          interval_value: is_periodic ? Number(interval_value) : 0,
          interval_unit: is_periodic ? interval_unit : "minutes",
          repeat_forever: Boolean(is_periodic && repeat_forever),
          repeat_count: is_periodic && repeat_forever ? -1 : Number(repeat_count || 1),
          overlap_value: is_periodic ? Number(overlap_value || 0) : 0,
          overlap_unit: is_periodic ? overlap_unit : "minutes",
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        throw new Error(`${response.status} - ${responseText}`);
      }

      alert("Задача парсинга новостей успешно создана.");
      setNewsTaskForm((prev) => ({
        ...prev,
        start_date: "",
        end_date: "",
      }));
    } catch (error) {
      console.error("Ошибка создания задачи news_tasks:", error);
      alert("Не удалось создать задачу парсинга новостей. Подробности в консоли.");
    } finally {
      setIsNewsTaskSubmitting(false);
    }
  };

  const fetchTgAuthStatus = async (validate = false) => {
    setIsTgAuthLoading(true);
    try {
      const response = await apiFetch(
        `${API_BASE_URL}/tg/auth/status?validate=${validate ? "true" : "false"}`
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Не удалось получить статус сессии Telegram.");
      }
      setTgAuthStatus(data);
    } catch (error) {
      console.error("Ошибка получения статуса сессии Telegram:", error);
      setTgAuthStatus({
        active: false,
        needs_refresh: true,
        error: error.message,
      });
    } finally {
      setIsTgAuthLoading(false);
    }
  };

  useEffect(() => {
    if (activeSection === "telegram") {
      fetchTgAuthStatus(false);
    }
  }, [activeSection]);

  const handleYoutubeTaskInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setYoutubeTaskForm((prev) => {
      const next = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "include_comments" && !checked) {
        next.force_refresh = false;
        next.max_comment_threads_per_video = "";
      }

      return next;
    });
  };

  const handleYoutubeTaskSubmit = async (event) => {
    event.preventDefault();
    const source = youtubeTaskForm.source.trim();

    if (!source) {
      alert("Укажите канал YouTube: ссылку, @handle, username или channel id.");
      return;
    }

    if (
      youtubeTaskForm.start_date &&
      youtubeTaskForm.end_date &&
      new Date(youtubeTaskForm.start_date) > new Date(youtubeTaskForm.end_date)
    ) {
      alert("Начальная дата не может быть позже конечной.");
      return;
    }

    if (
      youtubeTaskForm.is_periodic &&
      (!youtubeTaskForm.interval_value ||
        Number(youtubeTaskForm.interval_value) < 1 ||
        !youtubeTaskForm.interval_unit)
    ) {
      alert("Укажите интервал для периодической задачи YouTube.");
      return;
    }

    if (
      youtubeTaskForm.is_periodic &&
      !youtubeTaskForm.repeat_forever &&
      (!youtubeTaskForm.repeat_count || Number(youtubeTaskForm.repeat_count) < 1)
    ) {
      alert("Укажите количество повторов или включите бесконечный режим.");
      return;
    }

    setIsYoutubeTaskSubmitting(true);
    try {
      const payload = {
        source,
        api_key: youtubeTaskForm.api_key.trim() || undefined,
        start_date: youtubeTaskForm.is_periodic ? undefined : youtubeTaskForm.start_date || undefined,
        end_date: youtubeTaskForm.is_periodic ? undefined : youtubeTaskForm.end_date || undefined,
        include_comments: Boolean(youtubeTaskForm.include_comments),
        force_refresh: Boolean(youtubeTaskForm.include_comments && youtubeTaskForm.force_refresh),
        max_videos: normalizePositiveInteger(youtubeTaskForm.max_videos),
        max_comment_threads_per_video: youtubeTaskForm.include_comments
          ? normalizePositiveInteger(youtubeTaskForm.max_comment_threads_per_video)
          : undefined,
        interval_value: youtubeTaskForm.is_periodic ? Number(youtubeTaskForm.interval_value) : 0,
        interval_unit: youtubeTaskForm.is_periodic ? youtubeTaskForm.interval_unit : "minutes",
        repeat_forever: Boolean(youtubeTaskForm.is_periodic && youtubeTaskForm.repeat_forever),
        repeat_count:
          youtubeTaskForm.is_periodic && youtubeTaskForm.repeat_forever
            ? -1
            : Number(youtubeTaskForm.repeat_count || 1),
        overlap_value: youtubeTaskForm.is_periodic ? Number(youtubeTaskForm.overlap_value || 0) : 0,
        overlap_unit: youtubeTaskForm.is_periodic ? youtubeTaskForm.overlap_unit : "minutes",
      };

      const response = await apiFetch(`${YOUTUBE_API_BASE_URL}/parse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }

      await fetchTasks();
      alert("Задача парсинга YouTube создана.");
    } catch (error) {
      console.error("Ошибка создания задачи YouTube:", error);
      alert(`Не удалось создать задачу парсинга YouTube: ${error.message}`);
    } finally {
      setIsYoutubeTaskSubmitting(false);
    }
  };

  const handleTgTaskInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    const nextValue =
      name === "start_date" || name === "end_date" ? sanitizeDateValue(value) : value;
    setTgTaskForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : nextValue,
    }));
  };

  const handleTgAuthInputChange = (event) => {
    const { name, value } = event.target;
    setTgAuthForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTgAuthStart = async (event) => {
    event.preventDefault();
    const phone = tgAuthForm.phone.trim();

    if (!phone) {
      alert("Укажите номер телефона Telegram.");
      return;
    }

    setIsTgAuthLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/tg/auth/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          phone,
          ttl_minutes: Number(tgAuthForm.ttl_minutes) || 10,
        }),
      });
      const data = await response.json();
      if (!response.ok && response.status !== 202) {
        throw new Error(data.error || "Не удалось начать авторизацию Telegram.");
      }

      if (data.auth_id) {
        setTgAuthForm((prev) => ({ ...prev, auth_id: data.auth_id, code: "", password: "" }));
        setTgAuthNeedsPassword(false);
        alert("Код отправлен в Telegram. Введите его в поле подтверждения.");
      } else {
        alert(data.message || "Запрос авторизации поставлен в очередь.");
      }
    } catch (error) {
      console.error("Ошибка старта авторизации Telegram:", error);
      alert(`Не удалось начать авторизацию Telegram: ${error.message}`);
    } finally {
      setIsTgAuthLoading(false);
    }
  };

  const handleTgAuthConfirmCode = async (event) => {
    event.preventDefault();
    const authId = tgAuthForm.auth_id.trim();
    const code = tgAuthForm.code.trim();

    if (!authId || !code) {
      alert("Укажите auth_id и код из Telegram.");
      return;
    }

    setIsTgAuthLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/tg/auth/confirm_code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auth_id: authId, code }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Не удалось подтвердить код Telegram.");
      }

      if (data.needs_password) {
        setTgAuthNeedsPassword(true);
        alert("Telegram запросил пароль двухфакторной авторизации.");
        return;
      }

      if (data.ok || data.active) {
        setTgAuthNeedsPassword(false);
        setTgAuthForm((prev) => ({ ...prev, code: "", password: "" }));
        await fetchTgAuthStatus(false);
        alert("Сессия Telegram успешно активирована.");
      } else {
        alert(data.error || "Код не подтвержден.");
      }
    } catch (error) {
      console.error("Ошибка подтверждения кода Telegram:", error);
      alert(`Не удалось подтвердить код Telegram: ${error.message}`);
    } finally {
      setIsTgAuthLoading(false);
    }
  };

  const handleTgAuthConfirmPassword = async (event) => {
    event.preventDefault();
    const authId = tgAuthForm.auth_id.trim();
    const password = tgAuthForm.password;

    if (!authId || !password) {
      alert("Укажите auth_id и пароль двухфакторной авторизации.");
      return;
    }

    setIsTgAuthLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/tg/auth/confirm_password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ auth_id: authId, password }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Не удалось подтвердить пароль Telegram.");
      }

      if (data.ok || data.active) {
        setTgAuthNeedsPassword(false);
        setTgAuthForm((prev) => ({ ...prev, code: "", password: "" }));
        await fetchTgAuthStatus(false);
        alert("Сессия Telegram успешно активирована.");
      } else {
        alert(data.error || "Пароль не подтвержден.");
      }
    } catch (error) {
      console.error("Ошибка подтверждения пароля Telegram:", error);
      alert(`Не удалось подтвердить пароль Telegram: ${error.message}`);
    } finally {
      setIsTgAuthLoading(false);
    }
  };

  const handleTgAuthLogout = async () => {
    setIsTgAuthLoading(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/tg/auth/logout`, {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response));
      }
      setTgAuthStatus({ active: false, needs_refresh: true, reason: "logout" });
      alert("Сессия Telegram завершена.");
    } catch (error) {
      console.error("Ошибка выхода из сессии Telegram:", error);
      alert(`Не удалось завершить сессию Telegram: ${error.message}`);
    } finally {
      setIsTgAuthLoading(false);
    }
  };

  const handleTgTaskSubmit = async (event) => {
    event.preventDefault();
    const {
      channel,
      start_date,
      end_date,
      is_periodic,
      interval_value,
      interval_unit,
      repeat_forever,
      repeat_count,
      overlap_value,
      overlap_unit,
      use_coverage,
      include_discussion,
      discussion_limit_per_post,
      max_posts_for_discussion,
    } = tgTaskForm;

    const normalizedChannel = channel.trim();
    if (!normalizedChannel) {
      alert("Укажите канал Telegram: @channel, ссылку или идентификатор.");
      return;
    }

    if (!is_periodic && (!start_date || !end_date)) {
      alert("Укажите начальную и конечную даты.");
      return;
    }

    if (!is_periodic && (!isValidDateInput(start_date) || !isValidDateInput(end_date))) {
      alert("Год в датах должен быть четырехзначным, например 2026-05-13.");
      return;
    }

    if (!is_periodic && (start_date < MIN_TG_DATE || end_date > MAX_TG_DATE)) {
      alert("Укажите даты в диапазоне 1970-01-01 — 9999-12-31.");
      return;
    }

    if (!is_periodic && new Date(start_date) > new Date(end_date)) {
      alert("Начальная дата не может быть позже конечной.");
      return;
    }

    if (is_periodic && (!interval_value || Number(interval_value) < 1 || !interval_unit)) {
      alert("Укажите интервал для периодической задачи Telegram.");
      return;
    }

    if (is_periodic && !repeat_forever && (!repeat_count || Number(repeat_count) < 1)) {
      alert("Укажите количество повторов или включите бесконечный режим.");
      return;
    }

    const payload = {
      channel: normalizedChannel,
      start_date: is_periodic ? null : start_date,
      end_date: is_periodic ? null : end_date,
      interval_value: is_periodic ? Number(interval_value) : 0,
      interval_unit: is_periodic ? interval_unit : "minutes",
      repeat_forever: Boolean(is_periodic && repeat_forever),
      repeat_count: is_periodic && repeat_forever ? -1 : Number(repeat_count || 1),
      overlap_value: is_periodic ? Number(overlap_value || 0) : 0,
      overlap_unit: is_periodic ? overlap_unit : "minutes",
      use_coverage,
      include_discussion,
    };

    if (discussion_limit_per_post !== "") {
      payload.discussion_limit_per_post = Number(discussion_limit_per_post);
    }
    if (max_posts_for_discussion !== "") {
      payload.max_posts_for_discussion = Number(max_posts_for_discussion);
    }

    setIsTgTaskSubmitting(true);
    try {
      const response = await apiFetch(`${API_BASE_URL}/tg/parse`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const responseText = await response.text();
      let data = {};
      if (responseText) {
        try {
          data = JSON.parse(responseText);
        } catch {
          data = {};
        }
      }
      if (!response.ok) {
        throw new Error(
          data.error || data.message || responseText || "Не удалось создать задачу Telegram."
        );
      }

      setTgTaskResult(data);
      setTgTaskForm((prev) => ({ ...prev, channel: "" }));
      await fetchTasks();
      alert("Задача парсинга Telegram успешно создана.");
    } catch (error) {
      console.error("Ошибка создания задачи Telegram:", error);
      alert(`Не удалось создать задачу парсинга Telegram: ${error.message}`);
    } finally {
      setIsTgTaskSubmitting(false);
    }
  };

  const collectionTasks = useMemo(
    () =>
      [...tasks]
        .filter((task) => !task.data?.type)
        .sort(compareCollectionTasks),
    [tasks]
  );

  const taskStatusOptions = useMemo(
    () =>
      Array.from(new Set(collectionTasks.map((task) => normalizeTaskStatus(task))))
        .filter(Boolean)
        .sort(),
    [collectionTasks]
  );

  const taskTypeOptions = useMemo(
    () =>
      Array.from(new Set(collectionTasks.map((task) => getTaskType(task))))
        .filter(Boolean)
        .sort(),
    [collectionTasks]
  );

  const filteredCollectionTasks = useMemo(() => {
    const query = taskSearch.trim().toLowerCase();
    return collectionTasks.filter((task) => {
      const status = normalizeTaskStatus(task);
      const type = getTaskType(task);
      const periodic = isPeriodicTask(task);

      if (taskStatusFilter && status !== taskStatusFilter) return false;
      if (taskTypeFilter && type !== taskTypeFilter) return false;
      if (taskPeriodicFilter === "periodic" && !periodic) return false;
      if (taskPeriodicFilter === "single" && periodic) return false;

      if (!query) return true;
      return [
        getTaskGroupTitle(task),
        getTaskGroupId(task),
        task.task_id,
        TASK_TYPE_LABELS[type] || type,
        status,
        getTaskErrorText(task),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [collectionTasks, taskPeriodicFilter, taskSearch, taskStatusFilter, taskTypeFilter]);

  const { page, totalPages, pageItems: paginatedTasks, goToPage } = usePagination(
    filteredCollectionTasks,
    10
  );

  return (
    <Container className="py-4 parsing-page">
      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <h1 className="card-title mb-4">Сбор данных</h1>
              <div className="d-flex flex-wrap gap-2 mb-4">
                <Button
                  variant={activeSection === "collection" ? "primary" : "outline-primary"}
                  onClick={() => setActiveSection("collection")}
                >
                  ВК
                </Button>
                <Button
                  variant={activeSection === "news" ? "primary" : "outline-primary"}
                  onClick={() => setActiveSection("news")}
                >
                  Новости
                </Button>
                <Button
                  variant={activeSection === "telegram" ? "primary" : "outline-primary"}
                  onClick={() => setActiveSection("telegram")}
                >
                  Telegram
                </Button>
                <Button
                  variant={activeSection === "youtube" ? "primary" : "outline-primary"}
                  onClick={() => setActiveSection("youtube")}
                >
                  YouTube
                </Button>
              </div>

              {activeSection === "collection" ? (
                <Form onSubmit={handleSubmit}>
                <Row className="mb-4">
                  <Col>
                    <GroupSelector
                      groups={groups}
                      isLoading={isGroupsLoading}
                      selectedIds={selectedGroups}
                      onChange={(ids) => setSelectedGroups(ids)}
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      label="Группы"
                      placeholder="Введите название или ID"
                      selectionType="multi"
                    />
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6}>
                    <Form.Group controlId="formApiKey">
                      <Form.Label className="fw-bold">API ключ/токен</Form.Label>
                      <Form.Control
                        type="text"
                        name="api_key"
                        placeholder="Введите API ключ"
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6} className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      name="is_periodic"
                      label="Запускать задачу по расписанию"
                      checked={parsingPageStore.isPeriodic}
                      onChange={(e) => parsingPageStore.setIsPeriodic(e.target.checked)}
                    />
                  </Col>
                </Row>

                {parsingPageStore.isPeriodic && (
                  <>
                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formIntervalValue">
                          <Form.Label className="fw-bold">Интервал (число)</Form.Label>
                          <Form.Control
                            type="number"
                            name="interval_value"
                            min="1"
                            value={parsingPageStore.intervalValue}
                            onChange={(e) => parsingPageStore.setIntervalValue(e.target.value)}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formIntervalUnit">
                          <Form.Label className="fw-bold">Единицы времени</Form.Label>
                          <Form.Control
                            as="select"
                            name="interval_unit"
                            value={parsingPageStore.intervalUnit}
                            onChange={(e) => parsingPageStore.setIntervalUnit(e.target.value)}
                          >
                            <option value="">Выберите единицы измерения</option>
                            <option value="minutes">Минуты</option>
                            <option value="hours">Часы</option>
                            <option value="days">Дни</option>
                            <option value="weeks">Недели</option>
                          </Form.Control>
                          <Form.Text className="text-muted">
                            Интервал между повторными запусками задачи.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formRepeatForever" className="mb-3">
                          <Form.Check
                            type="checkbox"
                            name="repeat_forever"
                            value="true"
                            label="Повторять бесконечно"
                            checked={parsingPageStore.repeatForever}
                            onChange={(e) => parsingPageStore.setRepeatForever(e.target.checked)}
                          />
                          <Form.Text className="text-muted">
                            Задача будет запускаться по расписанию, пока вы не отмените её вручную.
                          </Form.Text>
                        </Form.Group>
                        <Form.Group controlId="formRepeatCount">
                          <Form.Label className="fw-bold">Количество повторов</Form.Label>
                          <Form.Control
                            type="number"
                            name="repeat_count"
                            min="1"
                            value={parsingPageStore.reps}
                            disabled={parsingPageStore.repeatForever}
                            onChange={(e) => {
                              const value = e.target.value ? parseInt(e.target.value, 10) : "";
                              parsingPageStore.setReps(value);
                            }}
                          />
                          {parsingPageStore.repeatForever && (
                            <Form.Text className="text-muted">
                              В бесконечном режиме в backend уйдёт repeat_count = -1.
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formOverlapValue">
                          <Form.Label className="fw-bold">Перекрытие окна (число)</Form.Label>
                          <Form.Control
                            type="number"
                            name="overlap_value"
                            min="0"
                            value={parsingPageStore.overlapValue}
                            onChange={(e) => parsingPageStore.setOverlapValue(e.target.value)}
                          />
                          <Form.Text className="text-muted">
                            0 — без перекрытия. Например, 1 день захватит часть прошлого окна, чтобы добрать поздние комментарии.
                          </Form.Text>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formOverlapUnit">
                          <Form.Label className="fw-bold">Единицы перекрытия</Form.Label>
                          <Form.Control
                            as="select"
                            name="overlap_unit"
                            value={parsingPageStore.overlapUnit}
                            onChange={(e) => parsingPageStore.setOverlapUnit(e.target.value)}
                          >
                            <option value="minutes">Минуты</option>
                            <option value="hours">Часы</option>
                            <option value="days">Дни</option>
                            <option value="weeks">Недели</option>
                          </Form.Control>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}

                {!parsingPageStore.isPeriodic && (
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Group controlId="formDepth">
                        <Form.Label className="fw-bold">Глубина</Form.Label>
                        <Form.Control
                          type="number"
                          name="depth"
                          value={parsingPageStore.depth}
                          onChange={(e) => parsingPageStore.setDepth(e.target.value)}
                          min="1"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                )}

                  <Button variant="primary" type="submit" className="btn-lg">
                    Создать задачу
                  </Button>
                </Form>
              ) : activeSection === "news" ? (
                <Form onSubmit={handleNewsTaskSubmit}>
                  <Row className="mb-4">
                    <Col md={6}>
                      <Form.Group controlId="formNewsSource">
                        <Form.Label className="fw-bold">Группа источника</Form.Label>
                        <Form.Control
                          as="select"
                          name="source"
                          value={newsTaskForm.source}
                          onChange={handleNewsTaskInputChange}
                          required
                        >
                          {NEWS_SOURCE_OPTIONS.map((source) => (
                            <option key={source.id} value={source.label}>
                              {source.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>


                  <Row className="mb-4">
                    <Col>
                      <Form.Check
                        type="checkbox"
                        id="formNewsPeriodic"
                        name="is_periodic"
                        label="Запускать парсинг новостей по расписанию"
                        checked={newsTaskForm.is_periodic}
                        onChange={handleNewsTaskInputChange}
                      />
                    </Col>
                  </Row>
                  {!newsTaskForm.is_periodic && (
                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formNewsStartDate" className="date-field-inline">
                          <Form.Label className="fw-bold">С</Form.Label>
                          <Form.Control
                            type="date"
                            name="start_date"
                            value={newsTaskForm.start_date}
                            onChange={handleNewsTaskInputChange}
                            aria-label="Начальная дата"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formNewsEndDate" className="date-field-inline">
                          <Form.Label className="fw-bold">По</Form.Label>
                          <Form.Control
                            type="date"
                            name="end_date"
                            value={newsTaskForm.end_date}
                            onChange={handleNewsTaskInputChange}
                            aria-label="Конечная дата"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}


                  {newsTaskForm.is_periodic && (
                    <>
                      <Row className="mb-4">
                        <Col md={6}>
                          <Form.Group controlId="formNewsIntervalValue">
                            <Form.Label className="fw-bold">Интервал (число)</Form.Label>
                            <Form.Control
                              type="number"
                              name="interval_value"
                              value={newsTaskForm.interval_value}
                              onChange={handleNewsTaskInputChange}
                              min="1"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="formNewsIntervalUnit">
                            <Form.Label className="fw-bold">Единицы времени</Form.Label>
                            <Form.Control
                              as="select"
                              name="interval_unit"
                              value={newsTaskForm.interval_unit}
                              onChange={handleNewsTaskInputChange}
                              required
                            >
                              <option value="minutes">Минуты</option>
                              <option value="hours">Часы</option>
                              <option value="days">Дни</option>
                              <option value="weeks">Недели</option>
                            </Form.Control>
                            <Form.Text className="text-muted">
                              Первый запуск возьмёт период за один интервал назад.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mb-4">
                        <Col md={6}>
                          <Form.Group controlId="formNewsOverlapValue">
                            <Form.Label className="fw-bold">Перекрытие окна (число)</Form.Label>
                            <Form.Control
                              type="number"
                              name="overlap_value"
                              value={newsTaskForm.overlap_value}
                              onChange={handleNewsTaskInputChange}
                              min="0"
                            />
                            <Form.Text className="text-muted">
                              0 — без перекрытия. Если больше 0, уже найденные новости в этом окне обновятся.
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="formNewsOverlapUnit">
                            <Form.Label className="fw-bold">Единицы перекрытия</Form.Label>
                            <Form.Control
                              as="select"
                              name="overlap_unit"
                              value={newsTaskForm.overlap_unit}
                              onChange={handleNewsTaskInputChange}
                            >
                              <option value="minutes">Минуты</option>
                              <option value="hours">Часы</option>
                              <option value="days">Дни</option>
                              <option value="weeks">Недели</option>
                            </Form.Control>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mb-4">
                        <Col md={6}>
                          <Form.Check
                            type="checkbox"
                            id="formNewsRepeatForever"
                            name="repeat_forever"
                            label="Повторять бесконечно"
                            checked={newsTaskForm.repeat_forever}
                            onChange={handleNewsTaskInputChange}
                          />
                        </Col>
                        {!newsTaskForm.repeat_forever && (
                          <Col md={6}>
                            <Form.Group controlId="formNewsRepeatCount">
                              <Form.Label className="fw-bold">Количество успешных повторов</Form.Label>
                              <Form.Control
                                type="number"
                                name="repeat_count"
                                value={newsTaskForm.repeat_count}
                                onChange={handleNewsTaskInputChange}
                                min="1"
                                required
                              />
                            </Form.Group>
                          </Col>
                        )}
                      </Row>
                    </>
                  )}

                  <Button
                    variant="primary"
                    type="submit"
                    className="btn-lg"
                    disabled={isNewsTaskSubmitting}
                  >
                    {isNewsTaskSubmitting ? "Отправка..." : "Запустить парсинг новостей"}
                  </Button>
                </Form>
              ) : activeSection === "telegram" ? (
                <>
                  <div className="telegram-auth-panel mb-4">
                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                      <div>
                        <h2 className="card-title mb-1">Сессия Telegram</h2>
                        <div className={tgAuthStatus?.active ? "text-success" : "text-danger"}>
                          {tgAuthStatus?.active
                            ? `Активна${tgAuthStatus.phone ? `: ${tgAuthStatus.phone}` : ""}`
                            : "Не активна или требует обновления"}
                        </div>
                        {tgAuthStatus?.error && (
                          <div className="text-danger small mt-1">{tgAuthStatus.error}</div>
                        )}
                        {tgAuthStatus?.last_checked_at && (
                          <div className="text-muted small mt-1">
                            Проверена: {new Date(tgAuthStatus.last_checked_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          variant="outline-primary"
                          type="button"
                          onClick={() => fetchTgAuthStatus(true)}
                          disabled={isTgAuthLoading}
                        >
                          Проверить
                        </Button>
                        <Button
                          variant="outline-secondary"
                          type="button"
                          onClick={handleTgAuthLogout}
                          disabled={isTgAuthLoading || !tgAuthStatus?.active}
                        >
                          Выйти
                        </Button>
                      </div>
                    </div>

                    <Form onSubmit={handleTgAuthStart}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Group controlId="formTgPhone">
                            <Form.Label className="fw-bold">Телефон</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phone"
                              placeholder="+79990000000"
                              value={tgAuthForm.phone}
                              onChange={handleTgAuthInputChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group controlId="formTgAuthTtl">
                            <Form.Label className="fw-bold">TTL auth_id, мин.</Form.Label>
                            <Form.Control
                              type="number"
                              name="ttl_minutes"
                              min="1"
                              max="60"
                              value={tgAuthForm.ttl_minutes}
                              onChange={handleTgAuthInputChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={isTgAuthLoading}
                          >
                            Отправить код
                          </Button>
                        </Col>
                      </Row>
                    </Form>

                    <Form onSubmit={handleTgAuthConfirmCode}>
                      <Row className="mb-3">
                        <Col md={5}>
                          <Form.Group controlId="formTgAuthId">
                            <Form.Label className="fw-bold">auth_id</Form.Label>
                            <Form.Control
                              type="text"
                              name="auth_id"
                              value={tgAuthForm.auth_id}
                              onChange={handleTgAuthInputChange}
                              placeholder="Появится после отправки кода"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={3}>
                          <Form.Group controlId="formTgCode">
                            <Form.Label className="fw-bold">Код</Form.Label>
                            <Form.Control
                              type="text"
                              name="code"
                              value={tgAuthForm.code}
                              onChange={handleTgAuthInputChange}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={4} className="d-flex align-items-end">
                          <Button
                            variant="primary"
                            type="submit"
                            disabled={isTgAuthLoading}
                          >
                            Подтвердить код
                          </Button>
                        </Col>
                      </Row>
                    </Form>

                    {tgAuthNeedsPassword && (
                      <Form onSubmit={handleTgAuthConfirmPassword}>
                        <Row className="mb-3">
                          <Col md={5}>
                            <Form.Group controlId="formTgPassword">
                              <Form.Label className="fw-bold">Пароль 2FA</Form.Label>
                              <Form.Control
                                type="password"
                                name="password"
                                value={tgAuthForm.password}
                                onChange={handleTgAuthInputChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={4} className="d-flex align-items-end">
                            <Button
                              variant="primary"
                              type="submit"
                              disabled={isTgAuthLoading}
                            >
                              Подтвердить пароль
                            </Button>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </div>

                  <Form onSubmit={handleTgTaskSubmit}>
                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formTgChannel">
                          <Form.Label className="fw-bold">Канал Telegram</Form.Label>
                          <Form.Control
                            type="text"
                            name="channel"
                            placeholder="@channel или ссылка на канал"
                            value={tgTaskForm.channel}
                            onChange={handleTgTaskInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          name="is_periodic"
                          label="Периодический запуск"
                          checked={tgTaskForm.is_periodic}
                          onChange={handleTgTaskInputChange}
                        />
                      </Col>
                    </Row>

                    {!tgTaskForm.is_periodic && (
                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formTgStartDate" className="date-field-inline">
                          <Form.Label className="fw-bold">С</Form.Label>
                          <Form.Control
                            type="date"
                            name="start_date"
                            value={tgTaskForm.start_date}
                            min={MIN_TG_DATE}
                            max={MAX_TG_DATE}
                            onChange={handleTgTaskInputChange}
                            onInput={handleTgTaskInputChange}
                            aria-label="Начальная дата"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formTgEndDate" className="date-field-inline">
                          <Form.Label className="fw-bold">По</Form.Label>
                          <Form.Control
                            type="date"
                            name="end_date"
                            value={tgTaskForm.end_date}
                            min={MIN_TG_DATE}
                            max={MAX_TG_DATE}
                            onChange={handleTgTaskInputChange}
                            onInput={handleTgTaskInputChange}
                            aria-label="Конечная дата"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    )}

                    {tgTaskForm.is_periodic && (
                      <>
                        <Row className="mb-4">
                          <Col md={3}>
                            <Form.Group controlId="formTgIntervalValue">
                              <Form.Label className="fw-bold">Интервал</Form.Label>
                              <Form.Control
                                type="number"
                                min="1"
                                name="interval_value"
                                value={tgTaskForm.interval_value}
                                onChange={handleTgTaskInputChange}
                                required={tgTaskForm.is_periodic}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group controlId="formTgIntervalUnit">
                              <Form.Label className="fw-bold">Единицы</Form.Label>
                              <Form.Select
                                name="interval_unit"
                                value={tgTaskForm.interval_unit}
                                onChange={handleTgTaskInputChange}
                              >
                                <option value="minutes">Минуты</option>
                                <option value="hours">Часы</option>
                                <option value="days">Дни</option>
                                <option value="weeks">Недели</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group controlId="formTgOverlapValue">
                              <Form.Label className="fw-bold">Перекрытие</Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                name="overlap_value"
                                value={tgTaskForm.overlap_value}
                                onChange={handleTgTaskInputChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group controlId="formTgOverlapUnit">
                              <Form.Label className="fw-bold">Единицы</Form.Label>
                              <Form.Select
                                name="overlap_unit"
                                value={tgTaskForm.overlap_unit}
                                onChange={handleTgTaskInputChange}
                              >
                                <option value="minutes">Минуты</option>
                                <option value="hours">Часы</option>
                                <option value="days">Дни</option>
                                <option value="weeks">Недели</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>
                        <Row className="mb-4">
                          <Col md={6}>
                            <Form.Check
                              type="checkbox"
                              name="repeat_forever"
                              label="Повторять бесконечно"
                              checked={tgTaskForm.repeat_forever}
                              onChange={handleTgTaskInputChange}
                            />
                          </Col>
                          {!tgTaskForm.repeat_forever && (
                            <Col md={6}>
                              <Form.Group controlId="formTgRepeatCount">
                                <Form.Label className="fw-bold">Количество успешных повторов</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="1"
                                  name="repeat_count"
                                  value={tgTaskForm.repeat_count}
                                  onChange={handleTgTaskInputChange}
                                  required={tgTaskForm.is_periodic && !tgTaskForm.repeat_forever}
                                />
                              </Form.Group>
                            </Col>
                          )}
                        </Row>
                      </>
                    )}

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          name="use_coverage"
                          label="Учитывать уже загруженные интервалы"
                          checked={tgTaskForm.use_coverage}
                          onChange={handleTgTaskInputChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          name="include_discussion"
                          label="Загружать обсуждения"
                          checked={tgTaskForm.include_discussion}
                          onChange={handleTgTaskInputChange}
                        />
                      </Col>
                    </Row>

                    {tgTaskForm.include_discussion && (
                      <Row className="mb-4">
                        <Col md={6}>
                          <Form.Group controlId="formTgDiscussionLimit">
                            <Form.Label className="fw-bold">Лимит обсуждений на пост</Form.Label>
                            <Form.Control
                              type="number"
                              name="discussion_limit_per_post"
                              min="1"
                              value={tgTaskForm.discussion_limit_per_post}
                              onChange={handleTgTaskInputChange}
                              placeholder="Без ограничения"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="formTgMaxPostsForDiscussion">
                            <Form.Label className="fw-bold">Постов с обсуждениями</Form.Label>
                            <Form.Control
                              type="number"
                              name="max_posts_for_discussion"
                              min="1"
                              value={tgTaskForm.max_posts_for_discussion}
                              onChange={handleTgTaskInputChange}
                              placeholder="Без ограничения"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    <Button
                      variant="primary"
                      type="submit"
                      className="btn-lg"
                      disabled={isTgTaskSubmitting}
                    >
                      {isTgTaskSubmitting ? "Отправка..." : "Запустить парсинг Telegram"}
                    </Button>
                  </Form>

                  {tgTaskResult && (
                    <div className="mt-4 p-3 bg-light rounded">
                      <div className="fw-bold">Последняя задача Telegram</div>
                      <div>ID: {tgTaskResult.task_id}</div>
                      <div>Канал: {tgTaskResult.channel}</div>
                      <div>Статус: {tgTaskResult.status}</div>
                    </div>
                  )}
                </>
              ) : activeSection === "youtube" ? (
                <>
                  <Form onSubmit={handleYoutubeTaskSubmit}>
                    <Row className="mb-4">
                      <Col md={8}>
                        <Form.Group controlId="formYoutubeSource">
                          <Form.Label className="fw-bold">Канал YouTube</Form.Label>
                          <Form.Control
                            type="text"
                            name="source"
                            placeholder="https://youtube.com/@channel, @handle или UC..."
                            value={youtubeTaskForm.source}
                            onChange={handleYoutubeTaskInputChange}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={8}>
                        <Form.Group controlId="formYoutubeApiKey">
                          <Form.Label className="fw-bold">YouTube API key</Form.Label>
                          <Form.Control
                            type="password"
                            name="api_key"
                            value={youtubeTaskForm.api_key}
                            onChange={handleYoutubeTaskInputChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          id="formYoutubePeriodic"
                          name="is_periodic"
                          label="Периодический запуск"
                          checked={youtubeTaskForm.is_periodic}
                          onChange={handleYoutubeTaskInputChange}
                        />
                      </Col>
                    </Row>

                    {!youtubeTaskForm.is_periodic && (
                      <Row className="mb-4">
                        <Col md={6}>
                          <Form.Group controlId="formYoutubeStartDate" className="date-field-inline">
                            <Form.Label className="fw-bold">С</Form.Label>
                            <Form.Control
                              type="date"
                              name="start_date"
                              value={youtubeTaskForm.start_date}
                              onChange={handleYoutubeTaskInputChange}
                              aria-label="Начальная дата"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="formYoutubeEndDate" className="date-field-inline">
                            <Form.Label className="fw-bold">По</Form.Label>
                            <Form.Control
                              type="date"
                              name="end_date"
                              value={youtubeTaskForm.end_date}
                              onChange={handleYoutubeTaskInputChange}
                              aria-label="Конечная дата"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    {youtubeTaskForm.is_periodic && (
                      <>
                        <Row className="mb-4">
                          <Col md={3}>
                            <Form.Group controlId="formYoutubeIntervalValue">
                              <Form.Label className="fw-bold">Интервал</Form.Label>
                              <Form.Control
                                type="number"
                                min="1"
                                name="interval_value"
                                value={youtubeTaskForm.interval_value}
                                onChange={handleYoutubeTaskInputChange}
                                required={youtubeTaskForm.is_periodic}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group controlId="formYoutubeIntervalUnit">
                              <Form.Label className="fw-bold">Единицы</Form.Label>
                              <Form.Select
                                name="interval_unit"
                                value={youtubeTaskForm.interval_unit}
                                onChange={handleYoutubeTaskInputChange}
                              >
                                <option value="minutes">Минуты</option>
                                <option value="hours">Часы</option>
                                <option value="days">Дни</option>
                                <option value="weeks">Недели</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group controlId="formYoutubeOverlapValue">
                              <Form.Label className="fw-bold">Перекрытие</Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                name="overlap_value"
                                value={youtubeTaskForm.overlap_value}
                                onChange={handleYoutubeTaskInputChange}
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group controlId="formYoutubeOverlapUnit">
                              <Form.Label className="fw-bold">Единицы</Form.Label>
                              <Form.Select
                                name="overlap_unit"
                                value={youtubeTaskForm.overlap_unit}
                                onChange={handleYoutubeTaskInputChange}
                              >
                                <option value="minutes">Минуты</option>
                                <option value="hours">Часы</option>
                                <option value="days">Дни</option>
                                <option value="weeks">Недели</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row className="mb-4">
                          <Col md={6}>
                            <Form.Check
                              type="checkbox"
                              id="formYoutubeRepeatForever"
                              name="repeat_forever"
                              label="Повторять бесконечно"
                              checked={youtubeTaskForm.repeat_forever}
                              onChange={handleYoutubeTaskInputChange}
                            />
                          </Col>
                          {!youtubeTaskForm.repeat_forever && (
                            <Col md={6}>
                              <Form.Group controlId="formYoutubeRepeatCount">
                                <Form.Label className="fw-bold">Количество успешных повторов</Form.Label>
                                <Form.Control
                                  type="number"
                                  min="1"
                                  name="repeat_count"
                                  value={youtubeTaskForm.repeat_count}
                                  onChange={handleYoutubeTaskInputChange}
                                  required={youtubeTaskForm.is_periodic && !youtubeTaskForm.repeat_forever}
                                />
                              </Form.Group>
                            </Col>
                          )}
                        </Row>
                      </>
                    )}

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Group controlId="formYoutubeMaxVideos">
                          <Form.Label className="fw-bold">Лимит видео</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            name="max_videos"
                            placeholder="Без лимита"
                            value={youtubeTaskForm.max_videos}
                            onChange={handleYoutubeTaskInputChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group controlId="formYoutubeMaxCommentThreads">
                          <Form.Label className="fw-bold">Веток комментариев на видео</Form.Label>
                          <Form.Control
                            type="number"
                            min="1"
                            name="max_comment_threads_per_video"
                            placeholder="Без лимита"
                            value={youtubeTaskForm.max_comment_threads_per_video}
                            onChange={handleYoutubeTaskInputChange}
                            disabled={!youtubeTaskForm.include_comments}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="mb-4">
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          id="formYoutubeIncludeComments"
                          name="include_comments"
                          label="Загружать комментарии"
                          checked={youtubeTaskForm.include_comments}
                          onChange={handleYoutubeTaskInputChange}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Check
                          type="checkbox"
                          id="formYoutubeForceRefresh"
                          name="force_refresh"
                          label="Перезагрузить комментарии заново"
                          checked={youtubeTaskForm.force_refresh}
                          onChange={handleYoutubeTaskInputChange}
                          disabled={!youtubeTaskForm.include_comments}
                        />
                      </Col>
                    </Row>

                    <Button
                      variant="primary"
                      type="submit"
                      className="btn-lg"
                      disabled={isYoutubeTaskSubmitting}
                    >
                      {isYoutubeTaskSubmitting ? "Отправка..." : "Запустить парсинг YouTube"}
                    </Button>
                  </Form>
                </>
              ) : null}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="tasks-table-header">
                <div>
                  <h2 className="card-title mb-1">Задачи на сбор данных</h2>
                  <div className="text-muted small">
                    Показано {filteredCollectionTasks.length} из {collectionTasks.length}
                  </div>
                </div>
              </div>

              <div className="tasks-table-filters">
                <Form.Group controlId="taskSearch">
                  <Form.Label>Поиск</Form.Label>
                  <Form.Control
                    type="search"
                    value={taskSearch}
                    placeholder="Источник, ID, ошибка"
                    onChange={(event) => setTaskSearch(event.target.value)}
                  />
                </Form.Group>

                <Form.Group controlId="taskStatusFilter">
                  <Form.Label>Статус</Form.Label>
                  <Form.Select
                    value={taskStatusFilter}
                    onChange={(event) => setTaskStatusFilter(event.target.value)}
                  >
                    <option value="">Все статусы</option>
                    {taskStatusOptions.map((status) => (
                      <option key={status} value={status}>
                        {TASK_STATUS_LABELS[status] || status}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="taskTypeFilter">
                  <Form.Label>Тип</Form.Label>
                  <Form.Select
                    value={taskTypeFilter}
                    onChange={(event) => setTaskTypeFilter(event.target.value)}
                  >
                    <option value="">Все типы</option>
                    {taskTypeOptions.map((type) => (
                      <option key={type} value={type}>
                        {TASK_TYPE_LABELS[type] || type}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group controlId="taskPeriodicFilter">
                  <Form.Label>Режим</Form.Label>
                  <Form.Select
                    value={taskPeriodicFilter}
                    onChange={(event) => setTaskPeriodicFilter(event.target.value)}
                  >
                    <option value="all">Все задачи</option>
                    <option value="periodic">Периодические</option>
                    <option value="single">Однократные</option>
                  </Form.Select>
                </Form.Group>
              </div>

              <div className="table-responsive">
                <Table hover className="tasks-table">
                  <thead>
                    <tr>
                      <th>Источник</th>
                      <th>Расписание</th>
                      <th>Следующий запуск</th>
                      <th>Статус</th>
                      <th>Последний запуск</th>
                      <th>Ошибка</th>
                      <th>Действия</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTasks.length > 0 ? (
                      paginatedTasks.map((task) => {
                        const taskType = getTaskType(task);
                        const status = normalizeTaskStatus(task);
                        const nextRunState = getNextRunState(task);
                        const errorText = getTaskErrorText(task);
                        const periodic = isPeriodicTask(task);

                        return (
                          <tr key={task.task_id} className={status.startsWith("failed") ? "task-row--failed" : ""}>
                            <td className="task-source-cell">
                              <div className="task-source-title">{getTaskGroupTitle(task)}</div>
                              <div className="task-source-meta">
                                <span>{TASK_TYPE_LABELS[taskType] || taskType}</span>
                                <span className="font-monospace">{getTaskGroupId(task)}</span>
                              </div>
                              <div className="task-source-id font-monospace">{task.task_id}</div>
                            </td>
                            <td>
                              <div className="task-schedule-main">{formatTaskInterval(task)}</div>
                              {periodic ? (
                                <>
                                  <div className="task-muted-line">
                                    Повторы: {formatRepeatCount(task)}
                                  </div>
                                  <div className="task-muted-line">
                                    Перекрытие: {formatOverlap(task)}
                                  </div>
                                </>
                              ) : null}
                            </td>
                            <td>
                              <span className={`task-next-run task-next-run--${nextRunState}`}>
                                {formatMoscowDateTime(task.next_run, "не запланировано")}
                              </span>
                            </td>
                            <td>
                              <span className={`task-status-badge task-status-badge--${TASK_STATUS_VARIANTS[status] || "dark"}`}>
                                {TASK_STATUS_LABELS[status] || task.status || "-"}
                              </span>
                            </td>
                            <td>{formatMoscowDateTime(getTaskLastRunValue(task), "не запускалась")}</td>
                            <td className="task-error-cell">
                              {errorText ? (
                                <div className="task-error-scroll">{errorText}</div>
                              ) : (
                                <span className="text-muted">нет</span>
                              )}
                            </td>
                            <td>
                              {periodic ? (
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  disabled={deletingTaskId === task.task_id}
                                  onClick={() => handleDeletePeriodicTask(task)}
                                >
                                  {deletingTaskId === task.task_id ? "Удаление..." : "Удалить"}
                                </Button>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-muted text-center py-4">
                          Задачи не найдены
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <div className="d-flex justify-content-center">
                  <PaginationControls
                    page={page}
                    totalPages={totalPages}
                    onPageChange={goToPage}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
});

export default ParsingPage;
